import React from 'react';
import { Space, Typography } from 'antd';
import { EnterOutlined } from '@ant-design/icons';

import { useAppContext } from '@components/AppContext';
import { type AppContext } from '@components/AppContext/types';

const LABEL_CURRENT = 'Current';
const LABEL_NAME = 'Name';

type UseColumnsConfigOptions = {
  onBeforeSwitch: () => Promise<void> | void;
  onAfterSwitch: () => Promise<void> | void;
  highlightedOrganizationId?: string;
};

type OrganizationRecord = {
  id: string;
};

type NameColumnProps = {
  key: string;
  title: string;
  render: (value: string, organization: OrganizationRecord) => React.ReactNode;
  isSearchable: boolean;
};

const useColumnsConfig = ({
  onBeforeSwitch,
  onAfterSwitch,
  highlightedOrganizationId,
}: UseColumnsConfigOptions): NameColumnProps[] => {
  const { identity, switchToOrganization } = useAppContext() as AppContext;

  const currentOrganizationId = identity?.organization?.id;

  const isCurrentOrganization = (id: string): boolean => id === currentOrganizationId;

  const nameColumnProps: NameColumnProps = {
    key: 'name',
    title: LABEL_NAME,
    render: (value: string, { id }: OrganizationRecord) => (
      (() => {
        const shouldShowEnterIcon = Boolean(
          highlightedOrganizationId && id === highlightedOrganizationId,
        );
        
        const left = isCurrentOrganization(id)
          ? (
            <Space>
              <Typography.Text strong>
                {value}
              </Typography.Text>
              <Typography.Text type="secondary">
                ({LABEL_CURRENT})
              </Typography.Text>
            </Space>
          )
          : (
            <Typography.Link
              strong
              onClick={() => Promise.resolve(onBeforeSwitch())
                .then(() => switchToOrganization(id))
                .then(() => onAfterSwitch())}
            >
              {value}
            </Typography.Link>
          );

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {left}
            {
              shouldShowEnterIcon && (
                <Typography.Text type="secondary">
                  <kbd>
                    <EnterOutlined />
                  </kbd>
                </Typography.Text>                
              )
            }
          </div>
        );
      })()
    ),
    isSearchable: true,
  };

  return [
    { ...nameColumnProps },
  ];
};

export default useColumnsConfig;
